/**
 * CAR-IA TopBar — the unified dark chrome, 1:1 with `.topbar` in
 * docs/html-prototypes/CAR-IA/car-ia-home.html: leading hamburger (mobile),
 * brand, horizontal nav, then the stacked icon+label action cluster
 * (Guardados / Vender / Ingresar) or the account pill once signed in.
 * Reusable across every buyer + seller surface; nav + auth state configurable.
 */
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { ChevronDownIcon, CoinIcon, HeartOutlineIcon, MenuIcon, UserIcon } from './icons';
import './caria-components.css';

export interface TopBarNavItem {
  label: string;
  to: string;
  /** Custom active test; defaults to exact/prefix match on `to`. */
  match?: (path: string) => boolean;
}

export interface TopBarProps {
  /** Brand label; the trailing "IA" is tinted lime (default "CAR-IA"). */
  brand?: string;
  /** Primary nav links (desktop). Omit to hide the nav. */
  nav?: TopBarNavItem[];
  /** Avatar initials; when set shows the account pill, else a "Entrar" button. */
  avatarInitials?: string;
  /** Account display name shown next to the avatar. */
  accountName?: string;
  /** Count on the "Guardados" action badge; omit or 0 to hide the badge. */
  savedCount?: number;
  /**
   * @deprecated The prototype bar has no bell — the badge lives on
   * "Guardados". Pass `savedCount` instead. Accepted so existing call sites
   * keep compiling; renders nothing.
   */
  notificationDot?: boolean;
  onMenu?: () => void;
  /** @deprecated No bell in the prototype chrome. */
  onNotifications?: () => void;
  onAvatar?: () => void;
  /** Replace the leading slot entirely (e.g. a back button on detail pages). */
  leading?: React.ReactNode;
}

/**
 * Default buyer navigation — the prototype's `.nav0`, in its order. Every
 * target is a live route (the last four are the ExamplePages scaffolds).
 */
export const BUYER_NAV: TopBarNavItem[] = [
  {
    label: 'Comprar',
    to: '/search',
    match: (p) => p === '/' || p.startsWith('/search') || p.startsWith('/vehicles'),
  },
  { label: 'Vender', to: '/publish', match: (p) => p.startsWith('/publish') },
  { label: 'Precios', to: '/guia-precios' },
  { label: 'Tasación', to: '/tasacion' },
  { label: 'Financiamiento', to: '/financiamiento' },
  { label: 'Reseñas', to: '/resenas' },
];

/** Seller / dealer navigation for the publishing surfaces. */
export const SELLER_NAV: TopBarNavItem[] = [
  { label: 'Mis anuncios', to: '/my-listings', match: (p) => p.startsWith('/my-listings') },
  { label: 'Publicar', to: '/publish', match: (p) => p.startsWith('/publish') },
  { label: 'Panel dealer', to: '/dealer', match: (p) => p.startsWith('/dealer') },
  { label: 'Guía de precios', to: '/guia-precios' },
];

const splitBrand = (brand: string): [string, string] => {
  const i = brand.toUpperCase().lastIndexOf('IA');
  if (i > 0) return [brand.slice(0, i), brand.slice(i)];
  return [brand, ''];
};

export const TopBar: React.FC<TopBarProps> = ({
  brand = 'CAR-IA',
  nav,
  avatarInitials,
  accountName,
  savedCount,
  onMenu,
  onAvatar,
  leading,
}) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [head, tail] = splitBrand(brand);

  const isActive = (item: TopBarNavItem) =>
    item.match ? item.match(pathname) : pathname === item.to || pathname.startsWith(item.to + '/');

  return (
    <header className="cia-topbar">
      <div className="cia-topbar-inner">
        {leading ?? (
          <button className="cia-topbar-hb" aria-label="Menú" onClick={onMenu}>
            <MenuIcon size={20} />
          </button>
        )}
        <button className="cia-topbar-brandbtn" onClick={() => navigate('/')} aria-label="Inicio">
          <span className="cia-topbar-text">
            {head}
            {tail && <b>{tail}</b>}
          </span>
        </button>

        {nav && nav.length > 0 && (
          <nav className="cia-topbar-nav" aria-label="Navegación">
            {nav.map((item) => (
              <button
                key={item.to}
                className={`cia-topbar-navlink${isActive(item) ? ' on' : ''}`}
                onClick={() => navigate(item.to)}
                aria-current={isActive(item) ? 'page' : undefined}
              >
                {item.label}
              </button>
            ))}
          </nav>
        )}

        <nav className="cia-topbar-acts" aria-label="Cuenta">
          <button className="cia-topbar-act" onClick={() => navigate('/saved')}>
            <span className="cia-topbar-act-ic">
              <HeartOutlineIcon size={22} strokeWidth={1.8} />
              {savedCount ? <span className="cia-topbar-badge">{savedCount}</span> : null}
            </span>
            <span className="cia-topbar-act-lb">Guardados</span>
          </button>
          <button className="cia-topbar-act" onClick={() => navigate('/publish')}>
            <span className="cia-topbar-act-ic">
              <CoinIcon size={22} strokeWidth={1.8} />
            </span>
            <span className="cia-topbar-act-lb">Vender</span>
          </button>
          {avatarInitials ? (
            <button className="cia-topbar-account" aria-label="Perfil" onClick={onAvatar}>
              <span className="cia-topbar-avatar">{avatarInitials}</span>
              {accountName && <span className="cia-topbar-name">{accountName}</span>}
              <span className="cia-topbar-account-chev">
                <ChevronDownIcon size={14} />
              </span>
            </button>
          ) : (
            <button className="cia-topbar-act" onClick={() => navigate('/login')}>
              <span className="cia-topbar-act-ic">
                <UserIcon size={22} strokeWidth={1.8} />
              </span>
              <span className="cia-topbar-act-lb">Ingresar</span>
            </button>
          )}
        </nav>
      </div>
    </header>
  );
};

/** Thin lime rail; place directly under the TopBar when a page wants extra emphasis. */
export const ColorRail: React.FC = () => <div className="cia-color-rail" />;

export default TopBar;
